import Link from "@components/common/Link";
import { Button, Icon } from "@justhome/react-ui";
import useJobTranslation from "@lib/hooks/translations/useJobTranslation";
import { getCareerPath } from "@lib/routing";
import clsx from "clsx";
import { useRouter } from "next/router";
import { FC } from "react";
import Badge, { BadgeBackground } from "../Badge";

type JobCardProps = {
  title: string;
  location?: string | null;
  employmentType?: string | null;
  category?: string | null;
  backgroundColor?: BadgeBackground | null;
  slug?: string | null;
  size?: "md" | "lg";
};

const JobCard: FC<JobCardProps> = ({
  title,
  location,
  employmentType,
  backgroundColor,
  category,
  slug,
  size = "lg",
}) => {
  const { i18n, t } = useJobTranslation();
  const router = useRouter();
  const href = getCareerPath(i18n.language, slug!);

  return (
    <Link
      href={href}
      className="h-full p-3s m:pt-3m m:px-3m m:pb-[28px] bg-neutral-0 rounded-3xs flex flex-col gap w-full border border-neutral-50"
    >
      <div className="flex gap-2m items-start justify-between">
        {category && <Badge background={backgroundColor}>{category}</Badge>}
        <div className="flex flex-col gap-3xs items-end mt-2xs">
          {employmentType && (
            <div className="flex items-center gap-xs">
              <Icon size={20} type="Handbag" className="!text-primary-800" />
              <span className="text-body-2 whitespace-nowrap">
                {employmentType}
              </span>
            </div>
          )}
          {location && (
            <div className="flex items-center gap-xs">
              <Icon size={20} type="LocalTwo" className="!text-primary-800" />
              <span className="text-body-2 whitespace-nowrap">{location}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex grow h-[76px] w-full">
        <h2
          className={clsx(
            "text-h2-serif text-primary-800 whitespace-normal",
            {
              "line-clamp-2": size === "lg",
              "line-clamp-1": size === "md",
            },
          )}
        >
          {title}
        </h2>
      </div>

      {size === "lg" && (
        <Button
          onClick={() => {
            router.push(href);
          }}
          variant="ghost"
          href={href}
          iconEnd="Right"
          paddingX={false}
        >
          {t("see_details")}
        </Button>
      )}
    </Link>
  );
};

export default JobCard;
